import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { FolderInfo, LabelRule } from '../../types';
import { LABEL_COLORS, labelView, fmtLabelStamp } from '../../utils/emailLabels';

const inputStyle = { width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' };
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 };
const ruleRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', alignItems: 'center', gap: '4px 10px' };
const ruleCaption: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)' };
const ruleHelp: React.CSSProperties = { gridColumn: 2, fontSize: 11, color: 'var(--text-tertiary)' };
const checkRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' };

// The filter's boxes, as typed (Gmail's "Create filter" form, less "Don't include chats").
type RuleForm = { from: string; to: string; subject: string; hasWords: string; notWords: string; sizeOp: 'gt' | 'lt'; size: string; sizeUnit: 'MB' | 'KB'; hasAttachment: boolean };
// Each text box: its field, caption, example shown while empty, and how to type it (Calem 2026-09-27 —
// server/src/services/labelRules.ts reads the boxes this way).
const RULE_BOXES = [
  ['from', 'eml_label_rule_from', 'eml_label_rule_addr_ph', ''],
  ['to', 'eml_label_rule_to', 'eml_label_rule_addr_ph', ''],
  ['subject', 'eml_label_rule_subject', 'eml_label_rule_subject_ph', 'eml_label_rule_subject_help'],
  ['hasWords', 'eml_label_rule_has_words', 'eml_label_rule_has_words_ph', 'eml_label_rule_has_words_help'],
  ['notWords', 'eml_label_rule_not_words', 'eml_label_rule_not_words_ph', 'eml_label_rule_not_words_help'],
] as const;
const ruleForm = (r?: LabelRule): RuleForm => ({
  from: r?.from || '', to: r?.to || '', subject: r?.subject || '', hasWords: r?.hasWords || '', notWords: r?.notWords || '',
  sizeOp: r?.sizeOp === 'lt' ? 'lt' : 'gt', size: r?.size ? String(r.size) : '', sizeUnit: r?.sizeUnit === 'KB' ? 'KB' : 'MB',
  hasAttachment: !!r?.hasAttachment,
});
// The boxes as the rule the server keeps — null when every box is empty (no filter).
const formRule = (f: RuleForm): LabelRule | null => {
  const r: LabelRule = {};
  for (const [k] of RULE_BOXES) if (f[k].trim()) r[k] = f[k].trim();
  if (f.size.trim()) Object.assign(r, { sizeOp: f.sizeOp, size: Number(f.size), sizeUnit: f.sizeUnit });
  if (f.hasAttachment) r.hasAttachment = true;
  return Object.keys(r).length ? r : null;
};
const swatch = (bg: string, on: boolean): React.CSSProperties => ({
  width: 24, height: 24, borderRadius: '50%', padding: 0, cursor: 'pointer', flexShrink: 0, position: 'relative',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: bg || 'var(--bg-surface)', border: bg ? 'none' : '1px solid var(--border-strong)',
  boxShadow: on ? '0 0 0 2px var(--bg-surface), 0 0 0 4px var(--accent)' : 'none',
});

// Rename / colour / delete one label. Local to PandaAI Email — Gmail's own label is never touched.
// Without a `folder` it is the New label dialog instead: that one DOES create the label on the mail
// server (a real Gmail label), so emails can be filed under it; only its colour stays local.
// On a person's own mailbox (account.personal — people's names and marketing@, never office@ / info@ or
// a shared mailbox) the Edit window also holds the label's filter: matching new mail gets the label and
// leaves the Inbox (server/src/services/labelRules.ts).
export default function LabelEditModal({ accountId, folder, nested = 0, onClose }: {
  accountId: string;
  folder?: FolderInfo;       // absent = create a new label
  nested?: number;           // sub-labels that leave the list with it on delete
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { accounts, labelOverrides, saveLabelOverride, createLabel, showToast } = useStore();
  const ov = folder ? labelOverrides[accountId]?.[folder.path] : undefined;
  const view = folder ? labelView(folder, labelOverrides[accountId]) : { name: '', color: '' };
  const [name, setName] = useState(view.name);
  const [color, setColor] = useState(view.color);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isCustom = !!color && !LABEL_COLORS.some(c => c.toLowerCase() === color.toLowerCase());
  const personal = !!folder && !!accounts.find(a => a.id === accountId)?.personal;
  const [rf0] = useState(() => ruleForm(ov?.rule));
  const [rf, setRf] = useState(rf0);
  const [sortExisting, setSortExisting] = useState(false);
  const setRule = (patch: Partial<RuleForm>) => { setRf(f => ({ ...f, ...patch })); setErr(''); };

  const save = async () => {
    if (busy || !folder) return;
    const rule = formRule(rf);
    const ruleChanged = personal && JSON.stringify(rule) !== JSON.stringify(formRule(rf0));
    const applyExisting = personal && sortExisting && !!rule;
    if (personal && rf.size.trim() && !(Number(rf.size) > 0)) { setErr(t('eml_label_rule_size_invalid')); return; }
    // Nothing changed → just close (don't stamp a new "Last changed by").
    if ((name.trim() || folder.name) === view.name && color.toLowerCase() === view.color.toLowerCase() && !ruleChanged && !applyExisting) { onClose(); return; }
    setBusy(true);
    const res = await saveLabelOverride(accountId, folder.path, {
      name: name.trim(), color: color || null,
      ...(ruleChanged ? { rule } : {}), ...(applyExisting ? { applyExisting } : {}),
    });
    setBusy(false);
    if (!res) return;
    if (res.sorting !== undefined) {
      showToast(res.sorting ? t('eml_label_rule_sorting', { n: res.sorting, name: name.trim() || folder.name }) : t('eml_label_rule_none'));
    }
    onClose();
  };
  const create = async () => {
    const n = name.trim();
    if (busy || !n) return;
    setBusy(true);
    setErr('');
    try {
      await createLabel(accountId, n, color);
      showToast(t('eml_label_created', { name: n }));
      onClose();
    } catch (e: any) {
      const p = e?.payload || {};
      setErr(p.code === 'exists' ? (p.deletedLabel ? t('eml_label_err_deleted', { name: p.deletedLabel }) : t('eml_label_err_exists', { name: p.label || n }))
        : p.code === 'parent_deleted' ? t('eml_label_err_parent_deleted', { name: p.parent })
        : p.code === 'invalid' ? t('eml_label_err_invalid')
        : p.code === 'reserved' ? t('eml_label_err_reserved')
        : p.code === 'retired' ? t('eml_label_err_retired')
        : p.code === 'not_personal' ? t('eml_label_err_not_personal')
        : t('eml_label_err_server', { msg: e?.message || '' }));
      setBusy(false);
    }
  };
  const submit = folder ? save : create;
  const remove = async () => {
    if (busy || !folder) return;
    const msg = nested > 0
      ? t('eml_label_delete_confirm_nested', { name: view.name, n: nested })
      : t('eml_label_delete_confirm', { name: view.name });
    if (!window.confirm(msg)) return;
    setBusy(true);
    const ok = await saveLabelOverride(accountId, folder.path, { deleted: true });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: personal ? 480 : 420, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="ti ti-tag" style={{ fontSize: 16, color: color || 'var(--accent)' }} aria-hidden />
              {folder ? t('eml_label_edit') : t('eml_label_new')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {folder ? t('eml_label_original', { name: folder.path }) : t('eml_label_new_in', { email: accounts.find(a => a.id === accountId)?.email || '' })}
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        {/* What this changes: edits stay in PandaAI Email; a new label is made on the mail server */}
        <div style={{ padding: '10px 20px', background: 'var(--accent-bg)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--accent-text)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <i className="ti ti-info-circle" style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          {folder ? t('eml_label_local_note') : t('eml_label_new_note')}
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
          <div>
            <label style={labelStyle}>{t('eml_label_name')}</label>
            <input style={inputStyle} value={name} onChange={e => { setName(e.target.value); setErr(''); }} placeholder={folder?.name} maxLength={100} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
          </div>
          <div>
            <label style={labelStyle}>{t('eml_label_color')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: 2 }}>
              <button onClick={() => setColor('')} title={t('eml_label_no_color')} style={swatch('', !color)}>
                <i className="ti ti-x" style={{ fontSize: 12, color: 'var(--text-tertiary)' }} aria-hidden />
              </button>
              {LABEL_COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)} title={c} style={swatch(c, color.toLowerCase() === c.toLowerCase())} />
              ))}
              {/* Any other colour — the native picker sits invisibly over a rainbow swatch */}
              <label title={t('eml_label_custom_color')} style={swatch(isCustom ? color : 'conic-gradient(#E24B4A, #BA7517, #1D9E75, #378ADD, #7F77DD, #C2417B, #E24B4A)', isCustom)}>
                <input type="color" value={color || '#1d9e75'} onChange={e => setColor(e.target.value)}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 'none', padding: 0 }} />
              </label>
            </div>
          </div>
          {/* Preview — exactly how the sidebar will show it */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 'var(--radius-md)', background: 'var(--bg-sidebar)', fontSize: 13, color: color || 'var(--text-secondary)' }}>
            <i className="ti ti-tag" style={{ fontSize: 14 }} aria-hidden />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name.trim() || folder?.name || t('eml_label_new')}</span>
          </div>
          {personal && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <i className="ti ti-filter" style={{ fontSize: 13 }} aria-hidden />{t('eml_label_rule_title')}
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('eml_label_rule_hint')}</div>
              </div>
              {RULE_BOXES.map(([k, key, ph, help]) => (
                <div key={k} style={ruleRow}>
                  <span style={ruleCaption}>{t(key)}</span>
                  <input style={inputStyle} value={rf[k]} maxLength={200} placeholder={t(ph)} onChange={e => { const v = e.target.value; setRf(f => ({ ...f, [k]: v })); setErr(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') save(); }} />
                  {help ? <div style={ruleHelp}>{t(help)}</div> : null}
                </div>
              ))}
              <div style={ruleRow}>
                <span style={ruleCaption}>{t('eml_label_rule_size')}</span>
                <div style={{ display: 'flex', gap: 6, minWidth: 0 }}>
                  <select style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={rf.sizeOp} onChange={e => setRule({ sizeOp: e.target.value === 'lt' ? 'lt' : 'gt' })}>
                    <option value="gt">{t('eml_label_rule_gt')}</option>
                    <option value="lt">{t('eml_label_rule_lt')}</option>
                  </select>
                  <input type="number" min="0" step="any" style={{ ...inputStyle, width: 80, flexShrink: 0 }} value={rf.size} onChange={e => setRule({ size: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') save(); }} />
                  <select style={{ ...inputStyle, width: 66, flexShrink: 0 }} value={rf.sizeUnit} onChange={e => setRule({ sizeUnit: e.target.value === 'KB' ? 'KB' : 'MB' })}>
                    <option value="MB">MB</option>
                    <option value="KB">KB</option>
                  </select>
                </div>
              </div>
              <label style={checkRow}>
                <input type="checkbox" checked={rf.hasAttachment} onChange={e => setRule({ hasAttachment: e.target.checked })} />{t('eml_label_rule_attachment')}
              </label>
              <label style={checkRow}>
                <input type="checkbox" checked={sortExisting} onChange={e => { setSortExisting(e.target.checked); setErr(''); }} />{t('eml_label_rule_existing')}
              </label>
            </div>
          )}
          {ov?.created_by_name && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('eml_label_created_by', { name: ov.created_by_name, time: fmtLabelStamp(ov.created_at) })}</div>
          )}
          {ov?.updated_by_name && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('eml_label_changed_by', { name: ov.updated_by_name, time: fmtLabelStamp(ov.updated_at) })}</div>
          )}
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {folder && (
            <button onClick={remove} disabled={busy} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid rgba(226,75,74,0.3)', background: 'transparent', color: '#E24B4A', cursor: busy ? 'default' : 'pointer' }}>
              <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden />{t('eml_label_delete')}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>{t('cancel')}</button>
          {folder ? (
            <button onClick={save} disabled={busy} style={{ padding: '7px 18px', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>{t('save')}</button>
          ) : (
            <button onClick={create} disabled={busy || !name.trim()} style={{ padding: '7px 18px', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: busy || !name.trim() ? 'default' : 'pointer', opacity: busy || !name.trim() ? 0.6 : 1 }}>
              {busy ? t('eml_label_creating') : t('eml_label_create')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
